import useSWR from 'swr';

import { findLocaleFromUrlAndCookie } from '@zooplus/commons-lang';

import type { CustomerType } from '@repo/types/customer-overview';
import type { MembershipOfferBenefitType } from '@repo/types/membership-offer';
import type {
  MembershipsComparison,
  MembershipInfo,
  MembershipsComparisonResponse,
} from '@repo/types/memberships-comparison';

export function useMembershipsComparison() {
  return useSWR(
    '/semiprotected/api/loyalty-management/memberships/comparison',
    async (...args): Promise<MembershipsComparison> => {
      const response = await fetch(...args, {
        headers: { locale: findLocaleFromUrlAndCookie() || 'en-GB' },
      });

      const data = (await response.json()) as MembershipsComparisonResponse;

      return transformMembershipComparisonResponse(data);
    },
    {
      errorRetryInterval: 1000,
      errorRetryCount: 3,
      dedupingInterval: 5000,
      revalidateOnFocus: false,
    },
  );
}

export function transformMembershipComparisonResponse(
  membershipsComparisonResponse: MembershipsComparisonResponse,
) {
  return {
    ...membershipsComparisonResponse,
    customerType: getCustomerType(membershipsComparisonResponse),
  };
}

export function getCustomerType(
  membershipsComparisonResponse: MembershipsComparisonResponse,
): CustomerType | undefined {
  if (Array.isArray(membershipsComparisonResponse.customerLoyaltyLevelTags)) {
    if (membershipsComparisonResponse.customerLoyaltyLevelTags.length === 0) {
      return undefined;
    }
    if (
      membershipsComparisonResponse.customerLoyaltyLevelTags.includes('PREMIUM') ||
      membershipsComparisonResponse.customerLoyaltyLevelTags.includes('SAVINGS_PLAN')
    ) {
      return 'premium';
    }
    if (membershipsComparisonResponse.customerLoyaltyLevelTags.includes('BASIC')) {
      return 'basic';
    }
  }

  // fallback to check customer level type if tags are not available
  if (
    membershipsComparisonResponse.customerLoyaltyLevelType === 'PREMIUM' ||
    membershipsComparisonResponse.customerLoyaltyLevelType === 'SP_MIGRATION'
  ) {
    return 'premium';
  }

  if (membershipsComparisonResponse.customerLoyaltyLevelType === 'BASIC') {
    return 'basic';
  }

  return undefined;
}

export function useMembershipBenefit(membershipInfo: MembershipInfo) {
  function getMembershipBenefitType<T extends MembershipOfferBenefitType['type']>(
    benefitType: T,
  ): Extract<MembershipOfferBenefitType, { type: T }> | undefined {
    return membershipInfo.benefits.find(
      (benefit): benefit is Extract<MembershipOfferBenefitType, { type: T }> =>
        benefit.type === benefitType,
    );
  }

  return { getMembershipBenefitType };
}
